// app/frontend/components/ui/Feedback/L2KNotification.ts
import { notifications, NotificationData } from "@mantine/notifications";
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
} from "@tabler/icons-react";
import { createElement } from "react";

type NotificationType = "success" | "error" | "warning" | "info";

interface L2KNotificationProps
  extends Omit<NotificationData, "color" | "icon"> {
  type?: NotificationType;
}

// Color configuration for each notification type
const notificationColors = {
  success: "#22c55e", // green
  error: "#ef4444", // red
  warning: "#FF9800", // L2K orange
  info: "#94a3b8", // gray
};

const getNotificationConfig = (type: NotificationType) => {
  const color = notificationColors[type];

  // Icon mapping
  const icons = {
    success: IconCheck,
    error: IconX,
    warning: IconAlertTriangle,
    info: IconInfoCircle,
  };

  const IconComponent = icons[type];

  return {
    color: color,
    icon: createElement(IconComponent, { size: 18, color: color }),
    styles: {
      root: {
        backgroundColor: "rgba(23, 23, 23, 0.95)",
        backdropFilter: "blur(16px)",
        borderRadius: "8px",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderLeft: `4px solid ${color}`,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
      },
      title: {
        color: "#F0F0F0",
        fontFamily: "'Public Sans', sans-serif",
        fontWeight: 600,
        fontSize: "14px",
      },
      description: {
        color: "#D0D0D0",
        fontFamily: "'Public Sans', sans-serif",
        fontWeight: 500,
        fontSize: "12px",
      },
      closeButton: {
        color: "#888",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
      },
      icon: {
        backgroundColor: `${color}26`, // 15% opacity
      },
    },
  };
};

/**
 * Show a styled L2K notification
 * @param type - "success" | "error" | "warning" | "info"
 * @param title - Notification title
 * @param message - Notification message
 */
export const showL2KNotification = ({
  type = "info",
  ...props
}: L2KNotificationProps) => {
  const config = getNotificationConfig(type);

  notifications.show({
    ...config,
    ...props,
    position: props.position || "top-right",
    autoClose: props.autoClose ?? 5000,
    // Merge custom styles with type-specific styles
    styles: {
      ...config.styles,
      ...(props.styles || {}),
    },
  });
};

// Convenience methods
export const l2kNotify = {
  success: (title: string, message?: string) =>
    showL2KNotification({ type: "success", title, message }),
  error: (title: string, message?: string) =>
    showL2KNotification({ type: "error", title, message }),
  warning: (title: string, message?: string) =>
    showL2KNotification({ type: "warning", title, message }),
  info: (title: string, message?: string) =>
    showL2KNotification({ type: "info", title, message }),
};

export default showL2KNotification;
